/**
 * DomSpellingLocaliser — a global DOM text-node transformer.
 *
 * Rather than wrap every hard-coded string in `<L>`, this mounts once
 * at the app root and rewrites British-spelled words to American in the
 * live DOM whenever the visitor's locale is "us". Handles all future
 * strings automatically — no per-file changes required.
 *
 * How it works:
 *   1. A single MutationObserver watches the whole document for text
 *      changes (React writes) and node additions/removals.
 *   2. When any batch of mutations fires, we walk the added text nodes
 *      (and their descendants) and apply `localiseSpelling` in place.
 *   3. Skips inputs, textareas, contentEditable elements, and <code>/
 *      <pre>/<script>/<style> where changing text would break code or
 *      copy behaviour.
 *   4. Cheap enough for 60fps thanks to batched processing +
 *      requestIdleCallback fallback for large trees.
 *
 * Non-goals: it does NOT rewrite `<input value>` (that would break form
 * state) or `placeholder=` attributes — those still need the `<L>`
 * wrapper or explicit locale-aware strings. Fine for MVP; ~99% of the
 * British spellings on the site live in visible text nodes.
 */
import { useEffect, useRef } from "react";
import { useEnglishLocale } from "@/lib/useEnglishLocale";
import { BRITISH_TO_AMERICAN, AMERICAN_TO_BRITISH } from "@/lib/spelling";

// Elements whose descendants we NEVER touch.
const SKIP_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT", "OPTION", "SCRIPT", "STYLE", "CODE", "PRE", "SVG"]);

function isEditable(node) {
  let el = node?.parentElement;
  while (el) {
    if (el.isContentEditable) return true;
    if (SKIP_TAGS.has(el.tagName)) return true;
    el = el.parentElement;
  }
  return false;
}

function buildRegex(map) {
  const keys = [...map.keys()].sort((a, b) => b.length - a.length);
  return new RegExp(`\\b(${keys.join("|")})\\b`, "gi");
}

function preserveCase(source, target) {
  if (!source) return target;
  if (source === source.toUpperCase()) return target.toUpperCase();
  if (source[0] === source[0].toUpperCase()) return target[0].toUpperCase() + target.slice(1);
  return target;
}

export default function DomSpellingLocaliser() {
  const locale = useEnglishLocale();
  const rxRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    const map = locale === "us" ? BRITISH_TO_AMERICAN : locale === "uk" ? AMERICAN_TO_BRITISH : null;
    if (!map) return;
    mapRef.current = map;
    rxRef.current = buildRegex(map);
    const rx = rxRef.current;

    const LOCALISE_ATTRS = ["placeholder", "title", "aria-label", "alt"];
    const localiseString = (s) => {
      if (!s || s.length < 3) return s;
      if (!rx.test(s)) { rx.lastIndex = 0; return s; }
      rx.lastIndex = 0;
      return s.replace(rx, (m) => preserveCase(m, map.get(m.toLowerCase()) || m));
    };
    const localiseAttrs = (el) => {
      if (!el || el.nodeType !== 1) return;
      if (SKIP_TAGS.has(el.tagName)) return;
      for (const a of LOCALISE_ATTRS) {
        if (!el.hasAttribute(a)) continue;
        const v = el.getAttribute(a);
        const next = localiseString(v);
        if (next !== v) el.setAttribute(a, next);
      }
    };

    const replaceInNode = (node) => {
      if (node.nodeType === 3) {
        // Text node
        if (isEditable(node)) return;
        const text = node.nodeValue;
        const next = localiseString(text);
        if (next !== text) node.nodeValue = next;
      } else if (node.nodeType === 1) {
        // Element node — recurse, skipping opaque elements
        if (SKIP_TAGS.has(node.tagName)) return;
        if (node.isContentEditable) return;
        localiseAttrs(node);
        const attrWalker = document.createTreeWalker(node, NodeFilter.SHOW_ELEMENT);
        let el;
        while ((el = attrWalker.nextNode())) localiseAttrs(el);
        const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, {
          acceptNode: (n) => (isEditable(n) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
        });
        let n;
        while ((n = walker.nextNode())) replaceInNode(n);
      }
    };

    // Initial pass — the app has already rendered by the time this
    // effect runs (StrictMode double-mount aside).
    replaceInNode(document.body);

    // Debounced observer for future writes.
    let pending = false;
    const queued = new Set();
    const flush = () => {
      pending = false;
      const nodes = [...queued];
      queued.clear();
      for (const n of nodes) {
        if (n && (n.isConnected ?? true)) replaceInNode(n);
      }
    };
    const schedule = () => {
      if (pending) return;
      pending = true;
      if (typeof requestIdleCallback === "function") requestIdleCallback(flush, { timeout: 120 });
      else setTimeout(flush, 60);
    };

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "characterData" && m.target) queued.add(m.target);
        else if (m.type === "childList") {
          for (const node of m.addedNodes) queued.add(node);
        } else if (m.type === "attributes" && m.target) {
          queued.add(m.target);
        }
      }
      if (queued.size) schedule();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: LOCALISE_ATTRS,
    });

    return () => {
      observer.disconnect();
      queued.clear();
    };
  }, [locale]);

  return null;
}
